import { useCallback } from 'react';
import { useBlocker } from 'react-router-dom';

interface UseUnsavedChangesGuardOptions {
  hasUnsavedChanges: boolean;
  onSave?: () => void | Promise<void>;
  onDiscard?: () => void;
}

export function useUnsavedChangesGuard({
  hasUnsavedChanges,
  onSave,
  onDiscard,
}: UseUnsavedChangesGuardOptions) {
  const blocker = useBlocker(hasUnsavedChanges);

  // Derive dialog visibility directly from blocker state — no separate useState needed.
  const showDialog = blocker.state === 'blocked';

  const handleSave = useCallback(async () => {
    await onSave?.();
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [onSave, blocker]);

  const handleDiscard = useCallback(() => {
    onDiscard?.();
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [onDiscard, blocker]);

  const handleCancel = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  return {
    showDialog,
    handleSave,
    handleDiscard,
    handleCancel,
  };
}
